/**
 * Path helpers for CRAWLABLE pagination.
 *
 * `?page=N` cannot be prerendered: the prerender writes files by path and nginx
 * `try_files` resolves `/?page=2` to the same `index.html` as `/`. Verified on
 * prod 2026-08-01 — `/` and `/?page=2` came back byte-identical (146,881 bytes,
 * the same 20 tender links). So every listing past its first 20 items had no
 * crawlable URL at all: 1,248 of 13,052 tender URLs were reachable by following
 * links, against 1,695 pages indexed in GSC.
 *
 * These helpers define the path form (`/page/2`, `/results/page/2`,
 * `/category/x/page/2`) that the route table, the pager and the prerender all
 * have to agree on. Keep them the single source of that shape.
 */

/**
 * Bare (locale-neutral) path for page `n` of the listing rooted at `basePath`.
 *
 * Page 1 is always the bare root — `/page/1` would be a duplicate of `/` with a
 * different URL, i.e. exactly the kind of near-dupe that gets a listing
 * consolidated away. Callers must therefore never link to `/page/1`.
 */
export function listingPageHref(basePath: string, n: number): string {
  if (n <= 1) return basePath;
  // '/' would otherwise produce '//page/2'.
  const root = basePath === '/' ? '' : basePath.replace(/\/$/, '');
  return `${root}/page/${n}`;
}

/**
 * 1-based page number for a listing, preferring the route path over the query
 * string. The path form (`/page/3`) is the crawlable one; `?page=3` remains for
 * filtered views, whose state lives in the query string anyway.
 *
 * `Number('abc')` is NaN — which is NOT nullish, so a `??` chain would let it
 * through and poison every downstream `offset` calculation. Both inputs are
 * user-editable URL segments, so both are validated as finite positive integers
 * and anything else degrades to page 1 rather than 400ing the API.
 */
export function resolveListingPage(
  routeParam: string | undefined,
  queryParam: string | null | undefined,
): number {
  const fromRoute = toPositiveInt(routeParam);
  if (fromRoute) return fromRoute;
  return toPositiveInt(queryParam) ?? 1;
}

function toPositiveInt(raw: string | null | undefined): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) return null;
  return n;
}
